import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { DolphinAuditTasksTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/DolphinAuditTasksTable';
import { TeamLeadBreadCrumbs } from 'react-scale/components/ExpertManagement/ExpertManagementPage/TeamLeadBreadCrumbs';
import {
  benchmarkCards,
  countCards,
  dolphinCards,
  qualityCards,
} from 'react-scale/components/ExpertManagement/ExpertManagementPage/cards';
import { useFetchAuditBatches } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAuditBatchesForUser';
import {
  StatContextState,
  apiStatContextSelector,
  dateRangeSelector,
  projectIdSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { useUserInfoStore } from 'react-scale/components/ExpertManagement/stores/userInfo.store';
import { shallow } from 'zustand/shallow';
import { CommentSection } from './CommentSection';
import { LastTaskAttemptsTable } from './LastTaskAttemptsTable';
import { PlatinumInfoSection } from './PlatinumInfoSection';
import { StatContextSelector } from './StatContextSelector';
import { StatisticRow } from './StatisticRow';
import { UserInfoCard } from './UserInfoCard';
import { WorkerSkillEntriesTable } from './WorkerSkillEntriesTable';
import { fetchStatsForExpert } from './api';

const selectors = (state: StatContextState) => ({
  projectId: projectIdSelector(state),
  dateRange: dateRangeSelector(state),
  apiStatContext: apiStatContextSelector(state),
});

export const IndividualExpertPage: React.FC = () => {
  const { projectId, dateRange, apiStatContext } = useStatContextStore(selectors, shallow);
  const userInfoStore = useUserInfoStore();
  const userInfo = userInfoStore.userInfo!;
  const expertId = userInfo.id;
  const stats = useQuery(
    [`statsForExpert`, expertId, projectId, dateRange],
    () => fetchStatsForExpert(apiStatContext, expertId),
    {
      staleTime: 60 * 60 * 1000,
    },
  );

  const isDolphin = useMemo(
    () =>
      stats?.data?.current?.dolphinQaTaskMetrics?.count ||
      stats?.data?.previous?.dolphinQaTaskMetrics?.count,
    [stats.data],
  );

  useFetchAuditBatches();

  const cardRows = useMemo(() => {
    if (!stats.data?.current) {
      return [];
    }
    const { current, previous } = stats.data;
    const defaultColumns = [
      ...countCards(current, previous),
      ...qualityCards(
        {
          qualityControlScore: current.gqaScore?.average,
          speedAuditScore: current.speedAuditAverage,
          averageSbqPercentage: current.sbqPercentage,
          averageFractalGuardian: current.averageFractalGuardian,
          qmsScore: current.qmsScore,
          qualityControlPdr: current.gqaPdr,
          speedAuditPdr: current.speedAuditPdr,
          gqaAndSpeedAuditScore: current.gqaAndSpeedAuditScore,
          qualityControlCount: current.gqaScore?.count,
          speedAuditCount: current.speedAuditStats?.count,
        },
        {
          qualityControlScore: previous?.gqaScore?.average,
          speedAuditScore: previous?.speedAuditAverage,
          averageSbqPercentage: previous?.sbqPercentage,
          averageFractalGuardian: previous?.averageFractalGuardian,
          qmsScore: previous?.qmsScore,
          qualityControlPdr: previous?.gqaPdr,
          speedAuditPdr: previous?.speedAuditPdr,
          gqaAndSpeedAuditScore: previous?.gqaAndSpeedAuditScore,
          qualityControlCount: previous?.gqaScore?.count,
          speedAuditCount: previous?.speedAuditStats?.count,
        },
      ),
      ...benchmarkCards(
        { averageBmAccuracy: current.averageBmAccuracy },
        { averageBmAccuracy: previous?.averageBmAccuracy },
      ),
    ];

    if (isDolphin) {
      defaultColumns.push(
        ...dolphinCards(current.dolphinQaTaskMetrics, previous?.dolphinQaTaskMetrics),
      );
    }

    return defaultColumns;
  }, [isDolphin, stats.data]);

  const taskAttemptIdAttemptAuditMap: { [key: string]: $FIXME } = useMemo(() => {
    const auditMap: Record<string, any> = {};
    for (const attemptAudit of stats.data?.current?.latestCreatedAtAttemptAudit || []) {
      auditMap[attemptAudit.auditLookup.id] = attemptAudit;
    }
    return auditMap;
  }, [stats.data]);

  const taskAttemptsWithPossibleAttemptAudit = useMemo(
    () =>
      stats.data?.current?.lastTaskAttempts.map(
        (taskAttempt: { id: string } & { [key: string]: $FIXME }) => {
          return {
            ...taskAttempt,
            attemptAudit: taskAttemptIdAttemptAuditMap[taskAttempt.id],
          };
        },
      ),
    [stats.data, taskAttemptIdAttemptAuditMap],
  );

  return (
    <div className="h-full flex flex-col items-start gap-4">
      <div className="flex w-full">
        <div className="flex flex-col gap-4 p-8 w-full">
          <TeamLeadBreadCrumbs />
          <StatContextSelector />
          <div className="flex justify-between gap-4">
            <div>
              <UserInfoCard userInfo={userInfo} />
            </div>
            {userInfo.teamLeadPodType.isPlatinum && (
              <div>
                <PlatinumInfoSection
                  isLoading={stats.isLoading}
                  data={stats.data?.current?.platinumInfo || {}}
                />
              </div>
            )}
            <div className="w-full">
              <StatisticRow
                title="User Activity"
                cards={cardRows}
                loadingCards={stats.isLoading ? 4 : 0}
              />
            </div>
          </div>
          <div className="flex self-stretch gap-4"></div>
          <LastTaskAttemptsTable
            isLoading={stats.isLoading}
            data={taskAttemptsWithPossibleAttemptAudit || []}
          />
          <WorkerSkillEntriesTable
            isLoading={stats.isLoading}
            data={stats.data?.current?.workerSkillEntries || []}
          />
          {isDolphin ? <DolphinAuditTasksTable expertId={expertId} /> : null}
        </div>
        <CommentSection targetId={userInfo.id} />
      </div>
    </div>
  );
};
